import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { createRoleAsync, fetchBranchDetailsAsync, fetchDepartmentDetailsAsync, fetchDesignationDetailsAsync, fetchGroupsDetailsAsync, fetchReportingManagerDetailsAsync, fetchReportingRoleDetailsAsync, fetchRolesDetailsAsync, fetchTypeAndSpecializationDetailsAsync } from '../thunks/RolesThunk';
import { Address, BankDetails, BaseRole, ConflictDetails, EKYCStatus, EducationDetail, LastEmployeeDetail, ProfessionalDetails, STATUS, TrainingCertificate, UserData } from './userDetailslice';

export interface GroupAndDepartment {
  description: string;
  id: number;
  name: string;
}
export interface TypeAndSpecialization {
  desc: string;
  id: number;
  name: string;
  multipleAllowed: boolean;
}
export interface Designation {
  id: number,
  name: string,
  isDefault: boolean
}
interface TypeAndSpecializationList {
  types: TypeAndSpecialization[];
  specializations: TypeAndSpecialization[]
};

const initialRolesDetailsState: RoleDetailsState = {
  departments: null,
  groups: null,
  rolesList: null,
  branchList: null,
  status: STATUS.IDLE,
  typeAndSpecializationList: null,
  designationList: null,
  reportingRoleList:null,
  reportingManagerList:null,
  loading: false,
  error: null,
  activeTabForEditFlow:null,
};
export interface Role {
  id: number;
  name: string;
  description: string;
  type: string;
  multipleBranchAccess: boolean;
  specializationMultiMandatory: boolean;
  bankDetailsMandatory:boolean
}
export interface Branch {
  sapDealerCode: string;
  tempDmsBranchSequence: number;
  name: string;
}

export interface ReportingRole {
  id: number;
  name: string;
 reportingRoles: reportingRole[];
}
export interface reportingRole 
  {
    id: number;
    name: string;
    default: boolean;
 }
// Common interface for shared properties
export interface BaseUser {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  email: string;
  primaryPhoneNumber: string;
  secondaryPhoneNumber: string;
  address: Address;
  gender: string;
  maritialStatus: string;
  dealerId: string;
  id: number;
  loginId: string;
  userType: string;
  status: string;
  hasDealerAssignedPhoneNumber: boolean;
  lastEmployeeDetails: LastEmployeeDetail[];
  bankDetails: BankDetails;
  educationDetails: EducationDetail[];
  professionalDetails: ProfessionalDetails;
  createdAt: string;
  createdBy: number;
  baseRoles: BaseRole[];
  multipleBranchAllowed: boolean;
  branches: any[];
  conflictDetails: ConflictDetails | null;
  trainingCertifications: TrainingCertificate[];
  applicationIds: number[];
  consentStatus: boolean;
  ekycStatus: EKYCStatus;
}

// ReportingManager interface extending BaseUser
export interface ReportingManager extends BaseUser {
  // Unique properties for ReportingManager
}

interface RoleDetailsState {
  departments: GroupAndDepartment[] | null;
  groups: GroupAndDepartment[] | null;
  rolesList: Role[] | null,
  branchList: Branch[] | null,
  typeAndSpecializationList: TypeAndSpecializationList | null,
  designationList: Designation[] | null,
  reportingManagerList: UserData[] | null,
  reportingRoleList: ReportingRole | null,
  status: string
  loading: boolean;
  error: string | null;
  activeTabForEditFlow: Number | null;
}
const rolesDetailsSlice = createSlice({
  name: 'rolesDetails',
  initialState: initialRolesDetailsState,
  reducers: {
    resetRoleCreationStatus: (state) => {
      state.status = STATUS.IDLE;
    },
    saveActiveTabIndex:(state,action: PayloadAction<number>) => {
      state.activeTabForEditFlow = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDepartmentDetailsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDepartmentDetailsAsync.fulfilled, (state, action) => {
        state.departments = action.payload;
        state.loading = false;
      })
      .addCase(fetchDepartmentDetailsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? null;
      })
      .addCase(fetchGroupsDetailsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGroupsDetailsAsync.fulfilled, (state, action) => {
        state.groups = action.payload;
        state.loading = false;
      })
      .addCase(fetchGroupsDetailsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? null;
      })
      .addCase(fetchRolesDetailsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRolesDetailsAsync.fulfilled, (state, action) => {
        state.rolesList = action.payload;
        state.loading = false;
      })
      .addCase(fetchRolesDetailsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? null;
      })
      .addCase(createRoleAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = STATUS.LOADING
      })
      .addCase(createRoleAsync.fulfilled, (state, action) => {
        console.log("inside role creation fulfilled");
        state.status = STATUS.SUCCESS;
        state.loading = false;
      })
      .addCase(createRoleAsync.rejected, (state, action) => {
        console.log("inside error", action.error.message ?? "null");
        state.loading = false;
        state.status = STATUS.FAILED;
        state.error = action.error.message ?? null;
      })
      .addCase(fetchTypeAndSpecializationDetailsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTypeAndSpecializationDetailsAsync.fulfilled, (state, action) => {
        state.typeAndSpecializationList = action.payload;
        state.loading = false;
      })
      .addCase(fetchTypeAndSpecializationDetailsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? null;
      })
      .addCase(fetchBranchDetailsAsync.fulfilled, (state, action) => {
        state.branchList = action.payload;
        state.loading = false;
      })
      .addCase(fetchBranchDetailsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? null;
      })
      .addCase(fetchBranchDetailsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDesignationDetailsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDesignationDetailsAsync.fulfilled, (state, action) => {
        state.designationList = action.payload;
        state.loading = false;
      })
      .addCase(fetchDesignationDetailsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? null;
      })
      .addCase(fetchReportingManagerDetailsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReportingManagerDetailsAsync.fulfilled, (state, action) => {
        state.reportingManagerList = action.payload;
        state.loading = false;
      })
      .addCase(fetchReportingManagerDetailsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? null;
      })
      .addCase(fetchReportingRoleDetailsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReportingRoleDetailsAsync.fulfilled, (state, action) => {
        state.reportingRoleList = action.payload;
        state.loading = false;
      })
      .addCase(fetchReportingRoleDetailsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? null;
      })

  },
});
export const { resetRoleCreationStatus, saveActiveTabIndex } = rolesDetailsSlice.actions;

export default rolesDetailsSlice.reducer;

